<!--
📌 Componente: App.vue
📂 Caminho: src/App.vue
📝 Descrição: Componente raiz da aplicação. Renderiza o `<router-view/>` e gerencia uma notificação global (`AlertNotifikation`). Disponibiliza a função `showNotification` via `provide()` para que componentes filhos possam disparar notificações.

👁️ Interface e Interação com o Usuário:
  - Renderiza a view atual através do `<router-view/>`.
  - Exibe um componente de notificação flutuante quando `notification.visible` é true.
  - Notificações são controladas por mensagem, cor e duração.

📥 Entrada (Props): Nenhuma

📤 Saída (Emits): Nenhuma

🔗 Dependências:
  - **Componentes Vue:** `@/components/AlertNotifikation.vue`
  - **APIs Vue:** `provide()` / `inject()` para distribuição de `showNotification`
  - **Router:** utiliza `<router-view/>` (Vue Router)
  - **Estilo/UI:** comportamento visual delegado a `AlertNotifikation`
-->

<template>
  <div
    class="min-h-screen bg-gradient-to-br from-cinza-200 to-azul-suave text-azul-profundo font-sans"
  >
    <router-view />
    <AlertNotifikation
      v-if="notification.visible"
      :message="notification.message"
      :color="notification.color"
      :duration="notification.duration"
    />
  </div>
</template>

<script>
/* Comentário: provider global de notificações com fallback i18n */
import AlertNotifikation from "@/components/AlertNotifikation.vue";
import { Network } from "@capacitor/network";
import { usePickupOfflineStore } from "@/stores/usePickupOfflineStore";

export default {
  components: { AlertNotifikation },
  data() {
    return {
      notification: {
        visible: false,
        message: "",
        color: "verde-suave",
        duration: 3000,
      },
      notifyTimer: null, // evita sobreposição de timers
      networkListener: null,
    };
  },
  async mounted() {
    this.networkListener = await Network.addListener(
      "networkStatusChange",
      (status) => {
        if (status.connected) {
          const offlineStore = usePickupOfflineStore();
          offlineStore.processQueue();
        }
      }
    );
  },
  beforeUnmount() {
    if (this.networkListener) {
      this.networkListener.remove();
    }
  },
  provide() {
    return { showNotification: this.showNotification };
  },
  methods: {
    // Aceita string ou objeto { type, text, key, message, color, duration }
    showNotification(payload, color = "verde-suave", duration = 3000) {
      const type2color = {
        success: "verde-suave",
        error: "vermelho-suave",
        warning: "amarelo-dourado",
        info: "azul-profundo",
      };

      let msg = "";
      let col = color;
      let dur = duration;

      if (payload && typeof payload === "object") {
        const { type, text, key, message: m, color: c, duration: d } = payload;
        msg = key ? this.$t(key) : String(text ?? m ?? "");
        col = c || type2color[type] || col;
        if (Number.isFinite(d)) dur = d;
      } else {
        msg = String(payload ?? "");
      }

      this.notification.message = msg;
      this.notification.color = col;
      this.notification.duration = dur;
      this.notification.visible = true;

      clearTimeout(this.notifyTimer);
      this.notifyTimer = setTimeout(() => {
        this.notification.visible = false;
      }, dur);
    },
  },
};
</script>
